/*
 * Apps & profiles page: pure model, no React and nothing that touches `window`. Which of the four states the list is in,
 * which app is selected, and what an app card says about its build providers.
 */
import type { App } from '@hangar/shared';

/** What the Applications card shows: skeletons, the error, the empty state or the cards. Cached data wins over a refetch. */
export type AppsViewState = 'loading' | 'error' | 'empty' | 'list';

export function appsViewState(q: { isLoading: boolean; isError: boolean; data: App[] | undefined }): AppsViewState {
  if (q.isLoading && !q.data) return 'loading';
  if (q.isError && !q.data) return 'error';
  return q.data && q.data.length > 0 ? 'list' : 'empty';
}

/** ` (3)` after the card title once the apps are known. */
export const appsCountLabel = (apps: App[] | undefined) => (apps ? ` (${apps.length})` : '');

/**
 * The project page's search: `?app=<slug>` selects an app; `?connect=1` opens Connect app once (the Overview's and
 * Getting started's "New app" link here).
 */
export function projectSearchOf(search: Record<string, unknown>): { app?: string; connect?: true } {
  const app = typeof search.app === 'string' && search.app ? search.app : undefined;
  const connect = search.connect === 1 || search.connect === '1' || search.connect === true;
  return { ...(app ? { app } : {}), ...(connect ? { connect: true as const } : {}) };
}

/** The app `?app=<slug>` names; the first one when the slug is absent or unknown. */
export const selectedApp = (apps: App[] | undefined, slug: string | undefined): App | undefined => apps?.find((a) => a.slug === slug) ?? apps?.[0];

export type ProviderTag = 'eas' | 'bitrise' | 'none';

/** The build providers an app is linked to (an EAS project and/or a Bitrise app), or `none`. */
export function providerTags(app: Pick<App, 'easProjectId' | 'bitriseAppSlug'>): ProviderTag[] {
  const tags: ProviderTag[] = [];
  if (app.easProjectId) tags.push('eas');
  if (app.bitriseAppSlug) tags.push('bitrise');
  return tags.length > 0 ? tags : ['none'];
}

/** ` · last build #42` after "created …", empty when the app never built. */
export const lastBuildSuffix = (app: Pick<App, 'lastBuildNumber'>) => (app.lastBuildNumber ? ` · last build #${app.lastBuildNumber}` : '');

/**
 * A long framework label ("Android native") keeps only its glyph in narrow cards (the 314 px desktop column, phones), so
 * the name and the slug keep their room; short ones ("Expo") always fit.
 */
export const frameworkLabelClass = (label: string) => (label.length > 8 ? '@max-[21rem]:hidden' : undefined);
